import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, Users2, Inbox } from "lucide-react";

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", tagline: "", primary_color: "#166534", accent_color: "#EAB308",
    logo_url: "", admin_name: "", admin_email: "", admin_password: "",
  });

  const load = () => api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/tenants", form);
      toast.success("Institution created");
      setOpen(false);
      setForm({ name: "", slug: "", tagline: "", primary_color: "#166534", accent_color: "#EAB308", logo_url: "", admin_name: "", admin_email: "", admin_password: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Institutions</h1>
          <p className="text-muted-foreground">Onboard and configure tenant institutions. Each has isolated data & branding.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-tenant-btn" className="brand-bg text-white"><Plus className="h-4 w-4 mr-1" />New Institution</Button></DialogTrigger>
          <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Institution</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="t-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Slug</Label><Input data-testid="t-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="e.g. mit" /></div>
              </div>
              <div><Label>Tagline</Label><Input data-testid="t-tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
              <div><Label>Logo URL</Label><Input data-testid="t-logo" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Primary color</Label><Input data-testid="t-primary" type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} /></div>
                <div><Label>Accent color</Label><Input data-testid="t-accent" type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} /></div>
              </div>
              <div className="border-t border-border pt-3 mt-2">
                <p className="text-sm font-medium mb-2">Institution Admin account</p>
                <div className="space-y-2">
                  <Input data-testid="t-admin-name" placeholder="Admin name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} />
                  <Input data-testid="t-admin-email" type="email" placeholder="Admin email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
                  <Input data-testid="t-admin-password" placeholder="Admin password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="t-create" onClick={create} className="brand-bg text-white">Create Institution</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tenants-list">
        {tenants.map((t) => (
          <Card key={t.id} className="p-5" data-testid={`tenant-${t.id}`}>
            <div className="flex items-center gap-3">
              {t.logo_url ? <img src={t.logo_url} alt="" className="h-11 w-11 object-contain" /> :
                <div className="h-11 w-11 rounded-md flex items-center justify-center text-white" style={{ background: t.primary_color }}><Building2 className="h-5 w-5" /></div>}
              <div className="min-w-0">
                <h3 className="font-heading font-semibold truncate">{t.name}</h3>
                <p className="text-xs text-muted-foreground">{t.tagline}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <span className="h-4 w-4 rounded-full border" style={{ background: t.primary_color }} />
              <span className="h-4 w-4 rounded-full border" style={{ background: t.accent_color }} />
              <span className="ml-auto text-xs text-muted-foreground flex items-center gap-3">
                <span className="flex items-center gap-1"><Users2 className="h-3 w-3" />{t.user_count}</span>
                <span className="flex items-center gap-1"><Inbox className="h-3 w-3" />{t.application_count}</span>
              </span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
